import React from "react";
import {
  ArrowLeftRight,
  Binoculars,
  AlarmClock,
  Inbox,
  Compass,
  SquareKanban,
  Radar,
  Columns2,
  Calendar,
  Megaphone,
  Scale,
  Sparkles,
  Newspaper,
  Handshake,
  Store,
  UserRoundCheck,
  Plus,
  ClipboardList,
  Sofa,
  Trophy,
} from "lucide-react";

// Single source of truth for the platform's menu icons (Marin's "unique icons"
// pass — Lucide outline canon from the design export). Every menu surface (the
// desktop + mobile mega-menu, the quick-access pills) resolves its glyph from
// THIS map by semantic key — no per-surface icon copies. The uniqueness
// constraint (zero duplicate glyphs across all role menus) is enforced here:
// each function maps to its own Lucide glyph.
//
// Keys are the function's semantic identity, not the surface it renders on, so
// the same key travels to every home of that function.

// Competitions — the table glyph from the Competitions handoff (UI-SPEC §3),
// verbatim SVG paths, colour via currentColor ("Farbe kommt vom Menü-Zustand,
// nicht aus dem Symbol"). Not a Lucide glyph: the handoff draws its own
// standings-table symbol; it accepts the same size/strokeWidth/className
// contract as the Lucide components so MenuIcon treats it identically.
// Stroke width is FIXED by the handoff (1.5 at 24/20, 1.6 at 16) — the
// incoming strokeWidth (MenuIcon's Lucide default 1.75) is deliberately
// ignored so the drawn glyph matches the spec'd SVG verbatim.
const CompetitionsGlyph = ({ size = 24, className = "" }) => {
  const sw = size <= 16 ? 1.6 : 1.5;
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      className={className}
      aria-hidden="true"
    >
      <rect
        x="3.25"
        y="4.25"
        width="17.5"
        height="15.5"
        rx="2"
        stroke="currentColor"
        strokeWidth={sw}
      />
      <path
        d="M3.25 9.25h17.5M3.25 14.5h17.5M9.5 9.25V19.75"
        stroke="currentColor"
        strokeWidth={sw}
        strokeLinecap="round"
      />
    </svg>
  );
};

export const MENU_ICONS = {
  // Canon functions (design export, main picks 1:1).
  AGENT_REQUESTS: ArrowLeftRight,
  SCOUT_REQUESTS: Binoculars,
  LAST_MINUTE: AlarmClock,
  SEARCH_REQUESTS: Inbox,
  DISCOVERY: Compass,
  TASKBOARD: SquareKanban,
  RADAR: Radar,
  COMPARE_PLAYERS: Columns2,
  EVENTS: Calendar, // the ONLY calendar glyph platform-wide
  ORGANIZERS: Megaphone,
  LAWYERS: Scale,
  WHATS_NEW: Sparkles,
  TRANSFER_NEWS: Newspaper,
  LOUNGE: Sofa, // Agent Lounge — final nav symbol (design 1q, chosen: Sofa)
  COMPETITIONS: CompetitionsGlyph, // Competitions — handoff table glyph (UI-SPEC §3), unique

  // Uncovered menu entries — kept/reassigned for a unique, semantic glyph
  // (reasoning in TP-MENUICONUPDATE/DECISIONS.md).
  FRIENDLIES: Handshake, // reserved for Friendlies (club-to-club)
  TOTW: Trophy, // Team of the Week — the round's best XI (unique glyph)
  LIVE_MARKET: Store, // storefront of players for sale/loan
  TRUSTED_AGENT: UserRoundCheck, // a trusted person you delegate a search to
  // Generic quick-access ACTION glyphs (not function identities — these chips
  // are actions, not menu functions, so they keep neutral action icons).
  POST_REQUEST: Plus,
  MY_REQUESTS: ClipboardList,
};

// Render helper: one place that fixes the rendered box + stroke so every menu
// surface draws its icon identically. `size` matches the surface it sits on
// (mega-menu rows: 22; quick-access pills: 15). strokeWidth 1.75 mirrors the
// design export's icon weight. Unknown keys render an empty box of the same
// size so layout never shifts.
export const MenuIcon = ({ name, size = 22, strokeWidth = 1.75, className = "" }) => {
  const Icon = MENU_ICONS[name];
  if (!Icon) {
    return (
      <span
        className={`inline-block flex-shrink-0 ${className}`}
        style={{ width: size, height: size }}
        aria-hidden="true"
      />
    );
  }
  return (
    <Icon size={size} strokeWidth={strokeWidth} className={className} aria-hidden="true" />
  );
};
